import { BreakpointObserver } from '@angular/cdk/layout';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, Component, computed, inject, input, model, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';

import { SelectableListboxItem } from './selectable-listbox.model';

let nextListboxId = 0;

/**
 * A fixed-height, virtualized selectable list. It intentionally does not use
 * MatChipListbox: Material tracks every projected option, which is incompatible
 * with CDK recycling option DOM nodes.
 */
@Component({
  selector: 'cipo-selectable-listbox',
  imports: [ScrollingModule, MatIconModule, TranslateModule],
  templateUrl: './selectable-listbox.component.html',
  styleUrl: './selectable-listbox.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectableListboxComponent<TKey = string> {
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly id = `cipo-selectable-listbox-${nextListboxId++}`;

  readonly viewport = viewChild(CdkVirtualScrollViewport);

  readonly options = input<SelectableListboxItem<TKey>[]>([]);
  readonly values = model<TKey[]>([]);
  readonly multiple = input(false);
  readonly columns = input(1);
  readonly disabled = input(false);
  readonly height = input('100%');
  readonly maxHeight = input('100%');
  readonly ariaLabel = input('');
  readonly testId = input<string>(null);

  /** All virtual rows are 48px tall: 40px option + 8px row gap. */
  readonly itemSize = 48;
  readonly activeIndex = signal(-1);
  private readonly smallScreen = toSignal(this.breakpointObserver.observe('(max-width: 959.98px)'), {
    initialValue: { matches: false, breakpoints: {} },
  });

  readonly columnCount = computed(() => (this.smallScreen().matches ? 1 : Math.max(1, this.columns() || 1)));
  readonly rows = computed(() => {
    const columnCount = this.columnCount();
    const options = this.options();
    const rows: SelectableListboxItem<TKey>[][] = [];

    for (let index = 0; index < options.length; index += columnCount) {
      rows.push(options.slice(index, index + columnCount));
    }

    return rows;
  });

  isSelected(key: TKey): boolean {
    return this.values().includes(key);
  }

  optionId(index: number): string {
    return `${this.id}-option-${index}`;
  }

  trackByRow(index: number): number {
    return index;
  }

  onFocus() {
    if (this.activeIndex() >= 0 || !this.options().length) {
      return;
    }

    const selectedIndex = this.options().findIndex(option => this.isSelected(option.key));
    this.setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }

  onOptionClick(index: number, option: SelectableListboxItem<TKey>) {
    if (this.disabled() || option.isDisabled) {
      return;
    }

    // The clicked option is already visible. Keep the virtual viewport stationary.
    this.activeIndex.set(index);
    this.toggle(option.key);
  }

  onKeydown(event: KeyboardEvent) {
    if (this.disabled()) {
      return;
    }

    const count = this.options().length;
    if (!count) {
      return;
    }

    const activeIndex = this.activeIndex() < 0 ? 0 : this.activeIndex();
    let nextIndex: number;

    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = activeIndex - 1;
        break;
      case 'ArrowRight':
        nextIndex = activeIndex + 1;
        break;
      case 'ArrowUp':
        nextIndex = activeIndex - this.columnCount();
        break;
      case 'ArrowDown':
        nextIndex = activeIndex + this.columnCount();
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = count - 1;
        break;
      case ' ':
      case 'Enter': {
        event.preventDefault();
        const option = this.options()[activeIndex];
        if (!option.isDisabled) {
          this.toggle(option.key);
        }
        return;
      }
      default:
        return;
    }

    event.preventDefault();
    this.setActiveIndex(Math.min(Math.max(nextIndex, 0), count - 1));
  }

  private setActiveIndex(index: number) {
    this.activeIndex.set(index);
    this.viewport()?.scrollToIndex(Math.floor(index / this.columnCount()), 'smooth');
  }

  private toggle(key: TKey) {
    const values = this.values();
    if (!this.multiple()) {
      this.values.set(this.isSelected(key) ? [] : [key]);
      return;
    }

    this.values.set(this.isSelected(key) ? values.filter(value => value !== key) : [...values, key]);
  }
}
